import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

export default function useDashboard() {
  const { api, AdminId } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [instructionTaskIds, setInstructionTaskIds] = useState(new Set());

  const emptyDashboardCounts = {
    all: 0,
    unassigned: 0,
    assigned: 0,
    acknowledged: 0,
    arrived: 0,
    "food is ready": 0,
    "picked up": 0,
    completed: 0,
    cancelled: 0,
  };

  const [dashboardTabCounts, setDashboardTabCounts] =
    useState(emptyDashboardCounts);

  const [dashboardTabsLoading, setDashboardTabsLoading] = useState(false);

 const shouldShowLocalError = (data) => {
  return (
    data?.responseCode !== "00" &&
    data?.responseCode !== "02" &&
    data?.responseCode !== "03" &&
    data?.responseCode !== "98" &&
    data?.responseCode !== "401"
  );
};

  // ================= AUTO ASSIGN TOGGLE =================
  // Original API used Bearer token, so use bearerApi
  const handleToggle = async () => {
    const newValue = !isChecked;
    setIsChecked(newValue);
    setLoading(true);

    try {
      const res = await api.post("/api/Task/v1.0/updateAutoAssignConfig", {
        configType: "auto_assign_status",
        configValue1: newValue ? "Y" : "N",
        configValue2: "",
        configValue3: "",
        configValue4: "",
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success("Auto assign updated");
        return true;
      }

      setIsChecked(!newValue);

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Update failed");
      }

      return false;
    } catch (err) {
      console.error("handleToggle error:", err);
      setIsChecked(!newValue);
      toast.error("Error updating config");
      return false;
    } finally {
      setLoading(false);
      ShowToggleStatus();
    }
  };

  // ================= GET TOGGLE STATUS =================
  // Original API used Bearer token, so use bearerApi
  const ShowToggleStatus = async () => {
    setLoading(true);

    try {
      const res = await api.post("/api/Task/v1.0/getAutoAssignConfig", {
        configType: "auto_assign_status",
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        const isOn = res.data?.data?.configValue === "Y";
        setIsChecked(isOn);
        return isOn;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch toggle status"
        );
      }

      return false;
    } catch (err) {
      console.error("ShowToggleStatus error:", err);
      toast.error("Error fetching toggle status");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ================= SYNC WITH ANLOOK =================
  // Original API used Basic token, so use api
  const SyncWithAnlook = async (taskId) => {
    if (!taskId) {
      toast.error("Invalid task ID");
      return false;
    }

    try {
      const res = await api.post(
        "/api/Task/v1.0/syncDeliveryDetailsWithPartner",
        {
          taskId,
          adminId: AdminId,
        }
      );

      if (res.data?.responseCode === "00") {
        toast.success(res.data.responseMessage || "Synced successfully");
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Sync failed");
      }

      return false;
    } catch (err) {
      console.error("SyncWithAnlook error:", err);
      toast.error("Error syncing with Anlook");
      return false;
    }
  };

  // ================= CUSTOMER INSTRUCTIONS =================
  // Original API used Basic token, so use api
  const fetchCustomerInstructions = async (fromDate, toDate) => {
    try {
      const res = await api.post(
        "/api/DriverTracker/v1.0/getCustomerAllTaskInstructionByDate",
        {
          adminId: String(AdminId),
          fromDate,
          toDate,
        }
      );

      if (res.data?.responseCode === "00") {
        const ids = new Set();

        (res.data?.data || []).forEach((i) => {
          ids.add(String(i.task_id));
        });

        setInstructionTaskIds(ids);
        return res.data?.data || [];
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch instructions"
        );
      }

      return [];
    } catch (err) {
      console.error("fetchCustomerInstructions error:", err);
      toast.error("Error fetching instructions");
      return [];
    }
  };

  // ================= DASHBOARD TAB COUNTS =================
  // Original API used Basic token, so use api
  const fetchDashboardTabsCount = useCallback(
    async (fromDate, toDate) => {
      if (!fromDate || !toDate) return null;

      setDashboardTabsLoading(true);

      try {
        const res = await api.post("/api/Task/v1.0/getDashBoardTabsCount", {
          fromDate,
          toDate,
        });

        if (res.data?.responseCode === "00") {
          const countData = res.data?.data?.[0] || {};

          const newCounts = {
            unassigned: Number(countData.unassigned || 0),
            assigned: Number(countData.assigned || 0),
            acknowledged: Number(countData.acknowledged || 0),
            arrived: Number(countData.arrived || 0),
            "food is ready": Number(countData.foodIsReady || 0),
            "picked up": Number(countData.pickedUp || 0),
            completed: Number(countData.successful || 0),
            cancelled: Number(countData.cancelled || 0),
          };

          newCounts.all =
            newCounts.unassigned +
            newCounts.assigned +
            newCounts.acknowledged +
            newCounts.arrived +
            newCounts["food is ready"] +
            newCounts["picked up"] +
            newCounts.completed +
            newCounts.cancelled;

          setDashboardTabCounts(newCounts);
          return newCounts;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(
            res.data?.responseMessage || "Failed to fetch tab counts"
          );
        }

        setDashboardTabCounts(emptyDashboardCounts);
        return null;
      } catch (err) {
        console.error("fetchDashboardTabsCount error:", err);
        toast.error("Error fetching dashboard tab counts");

        setDashboardTabCounts(emptyDashboardCounts);
        return null;
      } finally {
        setDashboardTabsLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    ShowToggleStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    isChecked,
    setIsChecked,
    instructionTaskIds,
    handleToggle,
    ShowToggleStatus,
    SyncWithAnlook,
    fetchCustomerInstructions,
    dashboardTabCounts,
    dashboardTabsLoading,
    fetchDashboardTabsCount,
  };
}